import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firestoreService } from '../../services/firestore.service';
import { cacheService } from '../../services/cache.service';
import { createDebouncedSync, syncImmediate, cancelSync, SYNC_TIMING } from '../../services/syncHelper';

// Rotating watermark prompts
export const JOURNAL_PROMPTS = [
    "Today I feel...",
    "This day...",
    "Dear diary...",
    "Thoughts & reflections",
    "What happened today...",
    "I'm grateful for...",
    "Today's highlights...",
    "Notes to self...",
];

// Get a consistent prompt for a given date (same date = same prompt)
export const getPromptForDate = (date) => {
    const dateStr = new Date(date).toDateString();
    let hash = 0;
    for (let i = 0; i < dateStr.length; i++) {
        hash = ((hash << 5) - hash) + dateStr.charCodeAt(i);
        hash = hash & hash;
    }
    return JOURNAL_PROMPTS[Math.abs(hash) % JOURNAL_PROMPTS.length];
};

// Format date for display in watermark
export const formatDateForWatermark = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

// Generate date key for storage
const getDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const useJournalStore = create(
    persist(
        (set, get) => ({
            // Journal entries indexed by date (YYYY-MM-DD)
            entries: {},

            // Global journal settings
            settings: {
                defaultBackgroundColor: '#fffef5', // Warm cream/paper color
                showWatermark: true,
                watermarkStyle: 'date', // 'date', 'prompt', or 'both'
            },

            // Firestore sync state
            userId: null,
            isInitialized: false,

            // ===== FIRESTORE SYNC (GRO-52) =====

            /**
             * Initialize journal from Firestore
             * @param {string} userId - Firebase user ID
             */
            initializeWithUser: async (userId) => {
                // Clear any stale entries first
                set({ userId, entries: {} });

                // Load from cache if available
                const cachedEntries = cacheService.getJournalEntries();
                if (cachedEntries && Object.keys(cachedEntries).length > 0) {
                    set({ entries: cachedEntries, isInitialized: true });
                    return;
                }

                // Entries will be loaded on-demand per date via loadEntry()
                set({ isInitialized: true });
            },

            /**
             * Sync a journal entry to Firestore (debounced)
             */
            syncJournalEntry: (dateKey) => {
                const { userId } = get();
                if (!userId) return;

                // Get entry at execution time (not creation time) to avoid stale closures
                createDebouncedSync(
                    `journal-${dateKey}`,
                    () => {
                        const entry = get().entries[dateKey];
                        if (entry) {
                            return firestoreService.saveJournalEntry(userId, dateKey, entry);
                        }
                        return Promise.resolve();
                    },
                    () => {
                        const entry = get().entries[dateKey];
                        if (entry) {
                            cacheService.setJournalEntry(dateKey, entry);
                        }
                    },
                    SYNC_TIMING.SLOW  // 2 seconds for typing
                )();
            },

            /**
             * Clear user data on logout
             */
            clearUserData: () => {
                // Cancel all pending journal syncs
                Object.keys(get().entries).forEach(key => cancelSync(`journal-${key}`));
                set({ userId: null, isInitialized: false, entries: {} });
            },

            // Get entry for a specific date
            getEntry: (date) => {
                const key = getDateKey(date);
                return get().entries[key] || null;
            },

            /**
             * Load entry from Firestore if not in local state
             */
            loadEntry: async (date) => {
                const key = getDateKey(date);
                const { userId, entries } = get();

                // Return cached entry if exists
                if (entries[key]) {
                    return entries[key];
                }

                // Try to load from Firestore
                if (userId) {
                    try {
                        const entry = await firestoreService.getJournalEntry(userId, key);
                        if (entry) {
                            set((state) => ({
                                entries: { ...state.entries, [key]: entry }
                            }));
                            cacheService.setJournalEntry(key, entry);
                            return entry;
                        }
                    } catch (error) {
                        console.error('Failed to load journal entry from Firestore:', error);
                    }
                }

                return null;
            },

            // Save/update entry for a specific date
            saveEntry: (date, content, backgroundColor = null) => {
                const key = getDateKey(date);
                const existingEntry = get().entries[key];

                const newEntry = {
                    date: key,
                    content: content,
                    backgroundColor: backgroundColor || existingEntry?.backgroundColor || get().settings.defaultBackgroundColor,
                    updatedAt: new Date().toISOString(),
                    createdAt: existingEntry?.createdAt || new Date().toISOString(),
                };

                set((state) => ({
                    entries: {
                        ...state.entries,
                        [key]: newEntry
                    }
                }));

                // Sync to Firestore (debounced)
                get().syncJournalEntry(key);
            },

            /**
             * Save entry immediately without debouncing (for unmount saves)
             * This bypasses the double-debounce issue where saves get cancelled
             */
            saveEntryImmediate: async (date, content, backgroundColor = null) => {
                const key = getDateKey(date);
                const { userId } = get();
                const existingEntry = get().entries[key];

                const newEntry = {
                    date: key,
                    content: content,
                    backgroundColor: backgroundColor || existingEntry?.backgroundColor || get().settings.defaultBackgroundColor,
                    updatedAt: new Date().toISOString(),
                    createdAt: existingEntry?.createdAt || new Date().toISOString(),
                };

                // Update local state
                set((state) => ({
                    entries: {
                        ...state.entries,
                        [key]: newEntry
                    }
                }));

                // Sync immediately (no debounce) if user is logged in
                if (userId) {
                    try {
                        await firestoreService.saveJournalEntry(userId, key, newEntry);
                        cacheService.setJournalEntry(key, newEntry);
                    } catch (error) {
                        console.error('Failed to save journal entry immediately:', error);
                    }
                }
            },

            // Update just the background color for a date
            setEntryBackgroundColor: (date, backgroundColor) => {
                const key = getDateKey(date);
                const existingEntry = get().entries[key];

                const newEntry = {
                    ...existingEntry,
                    date: key,
                    backgroundColor: backgroundColor,
                    updatedAt: new Date().toISOString(),
                    createdAt: existingEntry?.createdAt || new Date().toISOString(),
                    content: existingEntry?.content || null,
                };

                set((state) => ({
                    entries: {
                        ...state.entries,
                        [key]: newEntry
                    }
                }));

                // Sync to Firestore (debounced)
                get().syncJournalEntry(key);
            },

            // Delete entry for a specific date
            deleteEntry: async (date) => {
                const key = getDateKey(date);
                const { userId } = get();

                // Cancel any pending sync for this entry
                cancelSync(`journal-${key}`);

                set((state) => {
                    const { [key]: _removed, ...rest } = state.entries;
                    return { entries: rest };
                });

                // Delete from Firestore immediately
                if (userId) {
                    await syncImmediate(
                        `journal-delete-${key}`,
                        () => firestoreService.deleteJournalEntry(userId, key),
                        () => cacheService.removeJournalEntry(key)
                    );
                }
            },

            // Update global settings
            updateSettings: (newSettings) => {
                set((state) => ({
                    settings: { ...state.settings, ...newSettings }
                }));
            },

            // Check if a date has an entry
            hasEntry: (date) => {
                const key = getDateKey(date);
                const entry = get().entries[key];
                return entry && entry.content;
            },
        }),
        {
            name: 'grownups-journal-storage',
            version: 1,
        }
    )
);
